import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  TrendingUp,
  UserPlus,
  Target,
  Swords,
  Ticket,
  Users,
  Package,
  Settings,
  ChevronDown,
  ChevronRight,
  // Financials icons — restore alongside the commented NavGroup below.
  // Wallet,
  // PieChart,
  // FileText,
  // Building2,
  // BookOpen,
  // Receipt,
  // CreditCard,
  // Landmark,
  // BarChart3,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  end?: boolean;
  collapsed?: boolean;
}

function NavItem({ to, icon, label, badge, end, collapsed }: NavItemProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'sidebar-nav-item',
          isActive && 'active',
          collapsed && 'justify-center !px-2'
        )
      }
      style={{ position: 'relative' }}
      onMouseEnter={() => collapsed && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {icon}
      {!collapsed && (
        <>
          <span style={{ flex: 1 }}>{label}</span>
          {badge !== undefined && badge > 0 && (
            <span className="badge-primary" style={{ fontSize: '0.75rem', padding: '0.125rem 0.5rem' }}>{badge}</span>
          )}
        </>
      )}
      {collapsed && showTooltip && (
        <div
          className="bg-slate dark:bg-puddle"
          style={{
            position: 'absolute',
            left: '100%',
            marginLeft: '0.5rem',
            padding: '0.375rem 0.75rem',
            color: 'white',
            fontSize: '0.875rem',
            borderRadius: '0.375rem',
            whiteSpace: 'nowrap',
            zIndex: 9999,
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            pointerEvents: 'none',
          }}
        >
          {label}
        </div>
      )}
    </NavLink>
  );
}

interface NavGroupProps {
  label: string;
  icon: React.ReactNode;
  basePath: string;
  children: React.ReactNode;
  collapsed?: boolean;
}

function NavGroup({ label, icon, basePath, children, collapsed }: NavGroupProps) {
  const location = useLocation();
  const isActive = location.pathname.startsWith(basePath);
  const [isOpen, setIsOpen] = useState(isActive);
  const [showPopover, setShowPopover] = useState(false);

  if (collapsed) {
    return (
      <div
        style={{ position: 'relative', marginBottom: '0.25rem' }}
        onMouseEnter={() => setShowPopover(true)}
        onMouseLeave={() => setShowPopover(false)}
      >
        <button
          className={cn(
            'sidebar-nav-item w-full justify-center !px-2',
            isActive && 'text-pond dark:text-pond-light'
          )}
        >
          {icon}
        </button>
        {showPopover && (
          <div
            className="bg-white dark:bg-navy border border-gray dark:border-night-pond"
            style={{
              position: 'absolute',
              left: '100%',
              marginLeft: '0.5rem',
              top: 0,
              borderRadius: '0.5rem',
              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
              padding: '0.5rem 0',
              zIndex: 9999,
              minWidth: '12rem',
            }}
          >
            <div className="text-chalkboard dark:text-rain-cloud" style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {label}
            </div>
            <div style={{ marginTop: '0.25rem' }}>
              {children}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginBottom: '0.25rem' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'sidebar-nav-item w-full',
          isActive && 'text-pond dark:text-pond-light'
        )}
      >
        {icon}
        <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
        {isOpen ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>
      {isOpen && (
        <div className="border-l-2 border-gray-light dark:border-night-pond" style={{ marginLeft: '1rem', marginTop: '0.25rem', paddingLeft: '0.5rem' }}>
          {children}
        </div>
      )}
    </div>
  );
}

interface SubNavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  inPopover?: boolean;
}

function SubNavItem({ to, icon, label, inPopover }: SubNavItemProps) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors',
          isActive
            ? 'bg-pond/10 text-pond dark:bg-pond-light/20 dark:text-pond-light font-medium'
            : 'text-chalkboard hover:bg-pale-gray-2 dark:text-mirror dark:hover:bg-puddle/20',
          inPopover && 'mx-1'
        )
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <aside
      className={cn(
        'h-screen flex flex-col relative overflow-visible flex-shrink-0 transition-[width] duration-200 ease-in-out',
        'bg-white border-r border-gray',
        'dark:bg-navy dark:border-night-pond',
        collapsed ? 'w-[4.5rem]' : 'w-64'
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          'flex items-center border-b border-gray dark:border-night-pond',
          collapsed ? 'gap-0 px-2 py-5 justify-center' : 'gap-3 px-4 py-5 justify-start'
        )}
      >
        <img
          src="/BAFS-Logo-Small-RGB-Vertical.jpg"
          alt="BAFS"
          className="w-8 h-8 rounded-lg object-contain flex-shrink-0"
        />
        {!collapsed && (
          <span className="font-semibold text-lg text-slate dark:text-mist flex-1">
            Slope
          </span>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 rounded-md cursor-pointer text-chalkboard dark:text-rain-cloud bg-transparent border-none flex items-center justify-center hover:bg-pale-gray-2 dark:hover:bg-puddle/30"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <PanelLeft className="w-4 h-4" />
          ) : (
            <PanelLeftClose className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-visible py-4 scrollbar-hide">
        <NavItem
          to="/"
          icon={<LayoutDashboard className="w-5 h-5" />}
          label="Dashboard"
          end
          collapsed={collapsed}
        />

        <NavGroup
          label="Sales"
          icon={<TrendingUp className="w-5 h-5" />}
          basePath="/sales"
          collapsed={collapsed}
        >
          <SubNavItem
            to="/sales/prospects"
            icon={<UserPlus className="w-4 h-4" />}
            label="Prospects"
            inPopover={collapsed}
          />
          <SubNavItem
            to="/sales/opportunities"
            icon={<Target className="w-4 h-4" />}
            label="Opportunities"
            inPopover={collapsed}
          />
          <SubNavItem
            to="/sales/competitors"
            icon={<Swords className="w-4 h-4" />}
            label="Competitors"
            inPopover={collapsed}
          />
        </NavGroup>

        <NavItem
          to="/cases"
          icon={<Ticket className="w-5 h-5" />}
          label="Cases"
          collapsed={collapsed}
        />

        <NavItem
          to="/clients"
          icon={<Users className="w-5 h-5" />}
          label="Clients"
          collapsed={collapsed}
        />

        <NavItem
          to="/products"
          icon={<Package className="w-5 h-5" />}
          label="Products"
          collapsed={collapsed}
        />

        {/* Financials — hidden for launch. The pages behind these links are
            driven by hardcoded literals with no backend; see App.tsx.
        <NavGroup
        label="Financials"
        icon={<Wallet className="w-5 h-5" />}
        basePath="/financials"
        collapsed={collapsed}
        >
        <SubNavItem
        to="/financials"
        icon={<PieChart className="w-4 h-4" />}
        label="Dashboard"
        inPopover={collapsed}
        />
        <SubNavItem
        to="/financials/accounts"
        icon={<FileText className="w-4 h-4" />}
        label="Chart of Accounts"
        inPopover={collapsed}
        />
        <SubNavItem
        to="/financials/vendors"
        icon={<Building2 className="w-4 h-4" />}
        label="Vendors"
        inPopover={collapsed}
        />
        <SubNavItem
        to="/financials/ledger"
        icon={<BookOpen className="w-4 h-4" />}
        label="General Ledger"
        inPopover={collapsed}
        />
        <SubNavItem
        to="/financials/bills"
        icon={<Receipt className="w-4 h-4" />}
        label="Bills"
        inPopover={collapsed}
        />
        <SubNavItem
        to="/financials/payments"
        icon={<CreditCard className="w-4 h-4" />}
        label="Payments"
        inPopover={collapsed}
        />
        <SubNavItem
        to="/financials/banking"
        icon={<Landmark className="w-4 h-4" />}
        label="Bank Accounts"
        inPopover={collapsed}
        />
        <SubNavItem
        to="/financials/reports"
        icon={<BarChart3 className="w-4 h-4" />}
        label="Reports"
        inPopover={collapsed}
        />
        </NavGroup>
        */}
      </nav>

      {/* Settings - Bottom */}
      <div className="border-t border-gray dark:border-night-pond p-4">
        <NavItem
          to="/settings"
          icon={<Settings className="w-5 h-5" />}
          label="Settings"
          collapsed={collapsed}
        />
      </div>
    </aside>
  );
}
